import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const SecuritySection = () => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [backupCodes] = useState([
    'A1B2-C3D4-E5F6',
    'G7H8-I9J0-K1L2',
    'M3N4-O5P6-Q7R8',
    'S9T0-U1V2-W3X4',
    'Y5Z6-A7B8-C9D0'
  ]);

  const [activeSessions] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'Dallas, TX',
      ipAddress: '192.168.1.100',
      lastActive: '2025-11-06T16:20:00Z',
      current: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'Dallas, TX',
      ipAddress: '192.168.1.101',
      lastActive: '2025-11-06T14:30:00Z',
      current: false
    },
    {
      id: 3,
      device: 'Chrome on Android',
      location: 'Houston, TX',
      lastActive: '2025-11-05T09:15:00Z',
      current: false
    }
  ]);

  const [auditLog] = useState([
    {
      id: 1,
      action: 'Password Changed',
      timestamp: '2025-11-06T10:30:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/119.0.0.0'
    },
    {
      id: 2,
      action: 'Login Successful',
      timestamp: '2025-11-06T08:15:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/119.0.0.0'
    },
    {
      id: 3,
      action: 'Profile Updated',
      timestamp: '2025-11-05T16:45:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/119.0.0.0'
    },
    {
      id: 4,
      action: 'API Key Generated',
      timestamp: '2025-11-04T14:20:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Chrome/119.0.0.0'
    }
  ]);

  const [errors, setErrors] = useState({});

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordForm?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handlePasswordSubmit = () => {
    if (validatePasswordForm()) {
      console.log('Updating password...');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  };

  const handleEnable2FA = () => {
    if (!twoFactorEnabled) {
      setShowQRCode(true);
    } else {
      setTwoFactorEnabled(false);
      setShowQRCode(false);
    }
  };

  const handleConfirm2FA = () => {
    setTwoFactorEnabled(true);
    setShowQRCode(false);
    console.log('2FA enabled successfully');
  };

  const handleRevokeSession = (sessionId) => {
    console.log('Revoking session:', sessionId);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Password Change */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Lock" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Change Password</h3>
        </div>

        <div className="max-w-md space-y-4">
          <Input
            label="Current Password"
            type="password"
            name="currentPassword"
            value={passwordForm?.currentPassword}
            onChange={handlePasswordChange}
            error={errors?.currentPassword}
            required
          />
          
          <Input
            label="New Password"
            type="password"
            name="newPassword"
            value={passwordForm?.newPassword}
            onChange={handlePasswordChange}
            error={errors?.newPassword}
            description="Must be at least 8 characters long"
            required
          />
          
          <Input
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            value={passwordForm?.confirmPassword}
            onChange={handlePasswordChange}
            error={errors?.confirmPassword}
            required
          />
          
          <Button
            variant="default"
            onClick={handlePasswordSubmit}
            iconName="Save"
            iconPosition="left"
          >
            Update Password
          </Button>
        </div>
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={20} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Two-Factor Authentication</h3>
              <p className="text-sm text-text-secondary">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${twoFactorEnabled ? 'text-success' : 'text-text-secondary'}`}>
              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <Button
              variant={twoFactorEnabled ? "destructive" : "default"}
              onClick={handleEnable2FA}
              iconName={twoFactorEnabled ? "ShieldOff" : "ShieldCheck"}
              iconPosition="left"
            >
              {twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
            </Button>
          </div>
        </div>

        {showQRCode && (
          <div className="border border-border rounded-lg p-4 mb-4">
            <h4 className="font-medium text-text-primary mb-3">Setup Two-Factor Authentication</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-text-secondary mb-3">
                  Scan this QR code with your authenticator app:
                </p>
                <div className="w-48 h-48 bg-muted border border-border rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="QrCode" size={48} className="text-text-secondary mx-auto mb-2" />
                    <p className="text-xs text-text-secondary">QR Code</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm text-text-secondary mb-3">
                  Or enter this code manually:
                </p>
                <code className="block bg-muted p-3 rounded text-sm font-mono">
                  JBSWY3DPEHPK3PXP
                </code>
                <Button
                  variant="default"
                  onClick={handleConfirm2FA}
                  iconName="Check"
                  iconPosition="left"
                  className="mt-4"
                >
                  Confirm Setup
                </Button>
              </div>
            </div>
          </div>
        )}

        {twoFactorEnabled && (
          <div className="border border-border rounded-lg p-4">
            <h4 className="font-medium text-text-primary mb-3">Backup Codes</h4>
            <p className="text-sm text-text-secondary mb-3">
              Save these backup codes in a secure location. Each code can only be used once.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {backupCodes?.map((code, index) => (
                <code key={index} className="bg-muted p-2 rounded text-sm font-mono text-center">
                  {code}
                </code>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
            >
              Download Codes
            </Button>
          </div>
        )}
      </div>
      {/* Active Sessions */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Monitor" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Active Sessions</h3>
        </div>

        <div className="space-y-4">
          {activeSessions?.map((session) => (
            <div key={session?.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon 
                    name={session?.device?.includes('iPhone') || session?.device?.includes('Android') ? 'Smartphone' : 'Monitor'} 
                    size={20} 
                    className="text-text-secondary" 
                  />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-text-primary">{session?.device}</h4>
                    {session?.current && (
                      <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary">
                    {session?.location} • {session?.ipAddress}
                  </p>
                  <p className="text-xs text-text-secondary">
                    Last active {getRelativeTime(session?.lastActive)}
                  </p>
                </div>
              </div>
              {!session?.current && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRevokeSession(session?.id)}
                  iconName="X"
                >
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Security Audit Log */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-text-primary">Security Audit Log</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export Log
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Action</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">Timestamp</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">IP Address</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {auditLog?.map((entry) => (
                <tr key={entry?.id} className="border-b border-border">
                  <td className="py-3 px-4 text-sm font-medium text-text-primary">
                    {entry?.action}
                  </td>
                  <td className="py-3 px-4 text-sm text-text-secondary">
                    {formatTimestamp(entry?.timestamp)}
                  </td>
                  <td className="py-3 px-4 text-sm text-text-secondary font-mono">
                    {entry?.ipAddress}
                  </td>
                  <td className="py-3 px-4 text-sm text-text-secondary">
                    {entry?.userAgent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;